import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {NestedFrameController} from '@mintdata-wsp/common/controllers/spreadsheet/NestedFrameController';
import {FocusUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {MeshContextMenuProvider} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/mesh/MeshContextMenuProvider';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {InputView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/input/InputView';
import {KeyMap} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/Key';
import {MeshColumnsHeaders} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshColumnsHeaders';
import {MeshCopiedRange} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshCopiedRange';
import {MeshCurrentReferencedRange} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshCurrentReferencedRange';
import {MeshExtendedRange} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshExtendedRange';
import {MeshNamedRangeEditFrame} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshNamedRangeEditFrame';
import {MeshNamedRangesFrames} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshNamedRangesFrames';
import {MeshReferencedRanges} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshReferencedRanges';
import {MeshRowsHeaders} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshRowsHeaders';
import {MeshRowView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshRowView';
import {MeshScroll, MeshScrollDirection} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshScroll';
import {MeshSelectedRange} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshSelectedRange';
import {MeshSelectionFrame} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshSelectionFrame';
import {MeshTableKeyboardShortcuts} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/MeshTableKeyboardShortcuts';
import {NoteView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/notes/NoteView';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {FramePath} from '@mintdata/mint-kit/api/values/frames/Frame';
import {ContextMenu, ContextMenuConstructor} from '@mintdata/mint-kit/impl/components/contextMenu/ContextMenu';
import {NonPassiveEventListener} from '@mintdata/mint-kit/impl/events/Events';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import ResizeSensor from 'css-element-queries/src/ResizeSensor';
import {action, computed, observable, runInAction} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as React from 'react';
import {ActiveUserPointers} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/ActiveUserPointers';
import {MeshActiveUserSelections} from './MeshActiveUserSelections';
import {TextSelection} from '@mintdata-wsp/mint-kit/api/models/Text';

const meshTableStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(meshTableStyles);

interface IMeshTableProps
{
    ctx?:IWSPMeshContext;
    isNested?:boolean;
    theme?:IStyles;
    isScrollEnabled?:boolean;
    pathInFrame?:FramePath;
}

interface IMeshTableState
{
    isInitialized:boolean;
}

@inject('ctx')
@observer
export class MeshTable extends BaseComponentWithContext<IMeshTableProps, any>
{
    public static defaultProps = {
        isScrollEnabled: true,
        isNested: false
    };

    private _elementRef:HTMLDivElement = null;
    private _contentElementRef:HTMLDivElement = null;
    private _resizeSensor:ResizeSensor;
    private _lastActiveElement:HTMLElement = null;

    private _listenerDisposers:IDisposable[] = [];

    @observable
    private _state:IMeshTableState = {
        isInitialized: false
    };

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(meshTableStyles, theme || {})
        });
    }

    @computed
    private get _isCloudSheet():boolean
    {
        const {ctx} = this.props;
        const {main} = controllers.ui.spreadsheets;

        const sheet:AnySheet = main.findAnySheetById(ctx.sheetId, true);

        return sheet && sheet.isCloud;
    }

    @computed
    private get _isCloudLoading():boolean
    {
        const {ctx} = this.props;

        if( !this._isCloudSheet )
            return false;

        return ctx.sheetCtrl.isAnySlotLoading(ctx.sheetId);
    }

    @bind
    private _onElementRef(elementRef:HTMLDivElement):void
    {
        this._elementRef = elementRef;
    }

    @bind
    private _onFocus(event:React.FocusEvent<HTMLDivElement>):void
    {
        controllers.ui.spreadsheets.mesh.onFocusMesh(this.props.ctx);
    }

    @bind
    private _onContentElementRef(elementRef:HTMLDivElement):void
    {
        this._contentElementRef = elementRef;
    }

    @bind
    private _focusSelectionFrame():void
    {
        if( this._elementRef )
        {
            this._elementRef.focus();
            DomUtils.setNodeSelection(this._elementRef);
        }
    }

    @bind
    private _onPaste(event:React.ClipboardEvent<HTMLDivElement>):void
    {
        const {ctx: {sheetId, sheetCopyPasteCtrl}, isNested} = this.props;

        sheetCopyPasteCtrl.pasteSlotFromClipboard(sheetId, event, isNested);
    }

    @bind
    private _onKeyPress(event:React.KeyboardEvent<HTMLDivElement>):void
    {
        const {ctx} = this.props;

        const {isEditMode} = controllers.ui.spreadsheets.main;

        if( !isEditMode && !event.altKey && !event.ctrlKey )
        {
            const keyChar = KeyMap.getInstance().keyEventToChar(event);
            if( keyChar )
            {
                controllers.ui.spreadsheets.mesh.startEditingCell(ctx, keyChar);

                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    @bind
    private _onWheel(event:WheelEvent):void
    {
        event.preventDefault();
        event.stopPropagation();

        const {ctx, isScrollEnabled} = this.props;
        
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        const editorState = controllers.ui.spreadsheets.main.getEditorState();

        const inputState = editorState.input;

        if( !isScrollEnabled ) return;

        meshState.details.isScrolling = true;

        if( controllers.ui.spreadsheets.main.isEditMode && !!inputState.inputActivator )
        {
            controllers.ui.spreadsheets.main.setShowCellRefHint(true);

            meshState.details.inputSelection = new TextSelection(
                inputState.inputActivator.getSelection().startIndex, inputState.inputActivator.getSelection().endIndex
            );
        }

        const {scrollLeft, scrollTop} = meshState.details;
        const {width, height} = meshState.geometricSize;
        const contentWidth = width;
        const contentHeight = height;

        if( this._contentElementRef )
        {
            const {width, height} = this._contentElementRef.getBoundingClientRect();

            const ROW_HEIGHT = 32; // default row height
            const COL_WIDTH = 96; // default col width

            const THRESHOLD_Y_MIN = 4;
            const THRESHOLD_X_MIN = 10;

            const THRESHOLD_Y_MAX = ROW_HEIGHT / 2;
            const THRESHOLD_X_MAX = COL_WIDTH / 4;

            let deltaY = event.deltaY;
            let deltaX = event.deltaX;

            const absDeltaY = Math.abs(deltaY);
            const absDeltaX = Math.abs(deltaX);

            if( absDeltaY >= absDeltaX ) // vert scroll
            {
                if( absDeltaY < THRESHOLD_Y_MIN )
                {
                    deltaY = THRESHOLD_Y_MIN * Math.sign(deltaY);
                }
                else if( absDeltaY < THRESHOLD_Y_MAX )
                {
                    deltaY = THRESHOLD_Y_MAX * Math.sign(deltaY);
                }
                else
                {
                    deltaY = THRESHOLD_Y_MAX * Math.ceil(deltaY / THRESHOLD_Y_MAX);
                }
                deltaX = 0;
            }
            else // horiz scroll
            {
                if( absDeltaX < THRESHOLD_X_MIN )
                {
                    deltaX = THRESHOLD_X_MIN * Math.sign(deltaX);
                }
                else if( absDeltaX < THRESHOLD_X_MAX )
                {
                    deltaX = THRESHOLD_X_MAX * Math.sign(deltaX);
                }
                else
                {
                    deltaX = THRESHOLD_X_MAX * Math.ceil(deltaX / THRESHOLD_X_MAX);
                }
                deltaY = 0;
            }

            const newScrollTop:number = Math.max(Math.min(scrollTop + deltaY, contentHeight - height), 0);
            // console.log('event.deltaY =', event.deltaY, deltaY+'', newScrollTop);

            const newScrollLeft:number = Math.max(Math.min(scrollLeft + deltaX, contentWidth - width), 0);
            // console.log('event.deltaX =', event.deltaX, deltaX, newScrollLeft);

            // const newScrollLeft:number = Math.max(Math.min(scrollLeft + event.deltaX, contentWidth - width), 0);
            // const newScrollTop:number = Math.max(Math.min(scrollTop + event.deltaY, contentHeight - height), 0);

            requestAnimationFrame(() =>
            {
                controllers.ui.spreadsheets.mesh.setMeshScrollPosition(ctx, newScrollLeft, newScrollTop);
                
                const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(this.props.ctx);

                meshState.details.isScrolling = false;

                if( controllers.ui.spreadsheets.main.isEditMode && !!inputState.inputActivator )
                    inputState.inputActivator.setSelection(meshState.details.inputSelection);
            });
        }
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseDown(ctx, event);
    }

    @bind
    private _onMouseMove(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseMove(ctx, event);
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseLeave(ctx, event);
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseEnter(ctx, event);
    }

    @bind
    private _onMouseUp(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseUp(ctx, event);
    }

    @bind
    private _onMouseDoubleClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {meshMouse} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        meshMouse.onMouseDoubleClick(ctx, event);
    }

    @computed
    private get _contentStyle():React.CSSProperties
    {
        const {ctx} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !meshState )
            return null;

        const {width, height} = meshState.geometricSize;

        return {
            minWidth: `${width}px`,
            minHeight: `${height}px`
        };
    }

    @bind
    private _onContentResize():void
    {
        const {ctx, isNested} = this.props;

        const {mesh} = controllers.ui.spreadsheets;

        if( this._contentElementRef )
        {
            if( isNested )
                mesh.setNestedMeshVisibleSize(
                    ctx,
                    this._contentElementRef.clientWidth,
                    this._contentElementRef.clientHeight
                );
            else
                mesh.setMeshVisibleSize(
                    ctx,
                    this._contentElementRef.clientWidth,
                    this._contentElementRef.clientHeight
                );
        }
    }

    @action.bound
    private _handleMeshTableMouseMove(event:React.MouseEvent<HTMLElement>)
    {
        const {ctx} = this.props;

        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.isHovered = true;

        const {alignedScrollLeft, alignedScrollTop} = meshState;

        const {thisUser} = rootState.ui.visualDesigner.activeUsers;
        const {left: spreadsheetX, top: spreadsheetY } = event.currentTarget.getBoundingClientRect();

        thisUser.mouseX = event.clientX - spreadsheetX + alignedScrollLeft;
        thisUser.mouseY = event.clientY - spreadsheetY + alignedScrollTop;
        thisUser.cursorAt = ctx.isNested ? 'nestedframe' : 'spreadsheet';

        mesh.onMouseMoveEvent();
    }

    @action.bound
    private _handleVisibleRowsMouseLeave()
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.isHovered = false;
    }

    @bind
    private _setViewFocusActivator()
    {
        const viewFocusActivator = FocusUtil.focusActivator(this._focusSelectionFrame);

        const {ctx, isNested} = this.props;
        controllers.ui.spreadsheets.mesh.setViewFocusActivator(ctx, isNested, viewFocusActivator);
    }

    @bind
    private _resizeColumnsToContent()
    {
        const {ctx, isNested} = this.props;

        if( !isNested ) return;

        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !meshState ) return;

        const maxCols = meshState.size.numCols > 30 ? 30 : meshState.size.numCols;

        for( let colIndex:number = 0; colIndex < maxCols; colIndex++ )
            controllers.ui.spreadsheets.mesh.resizeColumnToContent(ctx, colIndex, true, true);
    }

    private _renderVisibleRows():Array<JSX.Element>
    {
        const {ctx, isNested} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !meshState )
            return null;

        const {visibleStartRowIndex, visibleEndRowIndex} = meshState;

        const rows:Array<JSX.Element> = [];

        for( let rowIndex:number = visibleStartRowIndex; rowIndex <= visibleEndRowIndex; rowIndex++ )
        {
            rows.push(<MeshRowView key={rowIndex} rowIndex={rowIndex} isNested={isNested} />);
        }

        return rows;
    }

    public componentWillMount():void
    {
        const {theme} = this.props;
        this._initStyles(theme);
    }

    public componentDidMount():void
    {
        this._onContentResize();

        if( controllers.ui.openDesign.isInSampleDesignEmbeddedMode() )
            controllers.ui.spreadsheets.mesh.onFocusMesh(this.props.ctx);
        else
            this._setViewFocusActivator();

        this._resizeColumnsToContent();

        if( this._contentElementRef )
        {
            this._resizeSensor = new ResizeSensor(this._contentElementRef, this._onContentResize);

            const listener = new NonPassiveEventListener(this._contentElementRef, 'wheel', this._onWheel);
            const disposer:IDisposable = this.context.nonPassiveEventListeners.add(listener);

            this._listenerDisposers.push(disposer);
        }

        if( this._isNeedShowLoader )
            setTimeout(() =>
            {
                runInAction(() =>
                {
                    this._state.isInitialized = true;
                });
            });
        else
            runInAction(() =>
            {
                this._state.isInitialized = true;
            });

        setTimeout(() =>
        {
            this._focusSelectionFrame();
        });
    }

    @computed
    private get _isNeedShowLoader():boolean
    {
        const {ctx, isNested} = this.props;

        if( !isNested ) return false;

        const {sheetId, sheetCtrl} = ctx;

        const numOfCells:number = (sheetCtrl as NestedFrameController).getNumOfCells(sheetId);

        return numOfCells > 1000;
    }

    public componentDidUpdate(prevProps:IMeshTableProps):void
    {
        if( this.props.ctx.sheetId !== prevProps.ctx.sheetId )
        {
            this._onContentResize();
            this._setViewFocusActivator();
        }
    }

    public componentWillReceiveProps(newProps:IMeshTableProps):void
    {
        if( this.props.theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    public componentWillUnmount():void
    {
        const {surfaceArea} = controllers.ui;

        if( this._resizeSensor )
        {
            this._resizeSensor.detach(this._onContentResize);
            this._resizeSensor = null;
        }

        this._listenerDisposers.forEach(disposer => disposer.dispose());

        if( !this.props.isNested )
            requestAnimationFrame(() => surfaceArea.focusSurfaceArea());
    }

    public render():JSX.Element
    {
        const {spreadsheets, bindings} = controllers.ui;

        const {
            ctx,
            isNested,
            isScrollEnabled,
            pathInFrame,
        } = this.props;

        const {isInitialized} = this._state;

        const TypedContextMenu:ContextMenuConstructor<any> = ContextMenu as any;

        const isBindingInProgress = bindings.isBindingInProgress();

        const {isEditMode, isShowCellRefHint} = spreadsheets.main.getEditorState();

        let tableContent:JSX.Element = (
            <div
                className={meshTableStyles.tableContentWrapper}
                ref={this._onContentElementRef}
            >
                {
                    (!isNested || isInitialized) &&
                    <div className={meshTableStyles.tableContent} style={this._contentStyle}
                        onMouseMove={this._handleMeshTableMouseMove}
                    >
                        <div className={meshTableStyles.visibleRowsWrapper}
                            onMouseLeave={this._handleVisibleRowsMouseLeave}
                        >
                            {this._renderVisibleRows()}
                            <MeshRowView rowIndex={null} isNested={isNested} />
                        </div>
                        {!isNested && <MeshActiveUserSelections />}
                        <MeshCurrentReferencedRange />
                        <MeshSelectedRange />
                        <MeshNamedRangesFrames isNested={isNested} pathInFrame={pathInFrame} />
                        <MeshExtendedRange />
                        <MeshSelectionFrame />
                        <MeshCopiedRange />
                        <MeshNamedRangeEditFrame />
                        <MeshReferencedRanges />
                        {!isNested && <ActiveUserPointers spreadsheet ctx={ctx} />}
                    </div>
                }
                {
                    this._isCloudLoading &&
                    <div className={cx('tableCellLoader', 'cloudView')}>
                        <Spinner size={SpinnerSize.Medium_24} style={SpinnerStyle.Blue} />
                        <div className={meshTableStyles.loaderText}>Evaluating...</div>
                    </div>
                }
            </div>
        );

        // TODO: maybe need some variation of feature flags
        /* if( !isNested )
        {
            tableContent = (
                <Dropzone
                    accept={spreadsheets.importTabs.getAcceptedExcelFileTypes().join(',')}
                    className={cx('dropzone')}
                    acceptClassName={cx('accept')}
                    rejectClassName={cx('accept')}
                    multiple={false}
                    disableClick={true}
                    onDrop={spreadsheets.importTabs.getExcelFilesSelectedHandler(spreadsheets.main.findAnySheetById(ctx.sheetId).isCloud)}
                >
                    {tableContent}
                    <div className={cx('dragOverPlaceholder')}>
                        {'Drop Excel file here to import it'}
                    </div>
                </Dropzone>
            );
        } */

        return (
            <MeshTableKeyboardShortcuts>
                <TypedContextMenu
                    contentClassName={cx('contextMenuContent')}
                    dataSource={MeshContextMenuProvider.contextMenuProvider(ctx)}
                    onShow={() => this._lastActiveElement = document.activeElement as HTMLElement}
                    onHide={() =>
                    {
                        if( this._lastActiveElement )
                            this._lastActiveElement.focus();
                    }}
                >
                    <div
                        data-type={MeshViewTargetType.Table}
                        className={cx('table', {isBindingInProgress, isInEditMode: isEditMode})}
                        tabIndex={-1}
                        ref={this._onElementRef}
                        onPaste={this._onPaste}
                        onKeyPress={this._onKeyPress}
                        onMouseDown={this._onMouseDown}
                        onMouseMove={this._onMouseMove}
                        onMouseUp={this._onMouseUp}
                        onMouseLeave={this._onMouseLeave}
                        onMouseEnter={this._onMouseEnter}
                        onDoubleClick={this._onMouseDoubleClick}
                        onFocus={this._onFocus}
                    >
                        <span className={meshTableStyles.selectionStub}>&nbsp;</span>
                        {
                            (!isNested || isInitialized) &&
                            <div className={meshTableStyles.tableHeaderRow}>
                                <div
                                    data-type={MeshViewTargetType.HeaderCorner}
                                    className={meshTableStyles.tableHeaderCorner}
                                >
                                    {''}
                                </div>
                                <MeshColumnsHeaders theme={meshTableStyles} isNested={isNested} />
                            </div>
                        }
                        <div className={meshTableStyles.tableContentRow}>
                            {
                                (!isNested || isInitialized) &&
                                <MeshRowsHeaders theme={meshTableStyles} isNested={isNested} />
                            }
                            {tableContent}
                            {
                                (!isNested || isInitialized) && isScrollEnabled &&
                                <MeshScroll direction={MeshScrollDirection.Vertical} />
                            }
                        </div>
                        {
                            (!isNested || isInitialized) && isScrollEnabled &&
                            <MeshScroll direction={MeshScrollDirection.Horizontal} />
                        }
                        {this._renderLoader()}
                    </div>
                </TypedContextMenu>
                {!isNested && <InputView isShowCellRefHint={isShowCellRefHint} />}
                {!isNested && <NoteView />}
            </MeshTableKeyboardShortcuts>
        );
    }

    private _renderLoader():JSX.Element
    {
        const {isNested} = this.props;
        const {isInitialized} = this._state;

        if( !isNested || isInitialized ) return null;

        let loaderStyle:SpinnerStyle = SpinnerStyle.White;
        let bgClass:string = '';

        if( (window as any).__enterNestedViewWithAltKey__ )
        {
            loaderStyle = SpinnerStyle.Blue;
            bgClass = 'bgLight';
        }

        return (
            <div
                className={cx('stubFrameDetails', bgClass)}
            >
                <div className={cx('loading')}>
                    <Spinner size={SpinnerSize.Large_64} style={loaderStyle} />
                </div>
            </div>
        );
    }
}
